<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { computed, ref, watch } from 'vue'

import { SuggestionJustification } from '@/misc/types'
import { useEvaluationFeedbackActions } from '@/stay-displayer/composables/useEvaluationFeedbackActions'
import JustificationSummary from '@/stay-displayer/diagnosis-lists/ApiJustificationSummary.vue'
import SvgSearchIcon from '@/stay-displayer/diagnosis-lists/SvgSearchIcon.vue'
import { MedicalActSuggestion } from '@/stay-displayer/medical-act/types'
import { useStore } from '@/store'
import { PredictedLabelType } from '@/store/modules/diagnosis/types'
import { StayHealthData } from '@/store/modules/stay/types'

const store = useStore()

const emit = defineEmits(['make-selection', 'promote-medical-act'])
const props = defineProps<{
  healthData: StayHealthData
  suggestion: MedicalActSuggestion
  readonly: boolean
  suggestionJustifications?: Record<string, SuggestionJustification> | null
}>()

const isLoading = ref(false)
const showJustificationSummary = ref(false)

const searchNames = computed(() =>
  props.suggestion.searches.map((sss) => sss.search.name).join(',')
)

const apiJustification = computed(() => {
  return props.suggestionJustifications?.[`${PredictedLabelType.MEDICAL_ACTS}:${props.suggestion.reference}`] ?? null
})

function promoteMedicalAct() {
  emit('promote-medical-act', props.suggestion)
}

function makeSelection() {
  emit('make-selection', {
    labelType: PredictedLabelType.MEDICAL_ACTS,
    labelReference: props.suggestion.reference,
    savedSearchIds: props.suggestion.searches.map((sss) => sss.search.id),
  })
}

function handleHealthDataSelection(mainContent: HealthDataDocument, scrollTarget: { docId: number, offset: number } | null = null) {
  store.commit('setScrollTarget', scrollTarget)
  store.commit('setMainContent', mainContent)
  makeSelection()
  showJustificationSummary.value = false
}

const evaluationFeedbackActions = useEvaluationFeedbackActions()

function flagJustification(payload) {
  store.dispatch('createFalseJustificationForSuggestion', payload)
}

function unflagJustification({ justificationId, docType, docId }) {
  store.dispatch('deleteFalseJustification', {
    justificationId,
    reportId: docType === 'report' ? docId : null,
    healthEntryId: docType === 'textualHealthEntry' ? docId : null,
  })
}

watch(showJustificationSummary, () => {
  makeSelection()
})
</script>

<template>
  <span
    v-if="!readonly && !isLoading"
    v-tooltip="$t('medicalAct.promote')"
    class="cp d-flex"
    @click="promoteMedicalAct"
  >
    <sancare-octicon
      name="arrow-left"
      :width="18"
      :height="18"
    />
  </span>
  <span
    v-else-if="isLoading"
    class="loader loader-xs"
    style="width: 12px; height: 12px; margin: 3px;"
  />
  <div class="px-2 flex-grow-1">
    <v-dropdown
      :shown="showJustificationSummary"
      placement="left"
      class="popover-container"
      popover-class="justification-popover"
      @show="showJustificationSummary = true"
    >
      <div>
        <h2 class="d-flex align-items-center">
          <span>{{ suggestion.reference }}</span>
          <span class="ms-auto d-flex align-items-center gap-1">
            <span
              v-if="suggestion.searches.length"
              v-tooltip="searchNames"
            >
              <svg-search-icon :is-revalo="false" />
            </span>
            <span v-if="suggestion.prediction">
              <sancare-octicon
                name="robot"
                :width="16"
              />
              <small class="ms-1">{{ Math.round(suggestion.prediction.confidence * 100) }}%</small>
            </span>
          </span>
        </h2>
        <small class="col act-data px-0">
          {{ suggestion.description }}
        </small>
      </div>
      <template #popper>
        <justification-summary
          :is-loading="false"
          :justification="apiJustification"
          :health-data="healthData"
          :label-type="PredictedLabelType.MEDICAL_ACTS"
          :label-reference="suggestion.reference"
          :no-value-label="$t('justification.noValue')"
          :feedback-actions="evaluationFeedbackActions"
          @select-health-data="handleHealthDataSelection"
          @flag-justification="flagJustification"
          @unflag-justification="unflagJustification"
        />
      </template>
    </v-dropdown>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
